import { type JSX, useState } from "react";
import { Link, useMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSignalR } from "@contexts/SignalRContext";
import { useConfig } from "@contexts/ConfigContext";
import { useThemeMode } from "@contexts/ThemeContext";
import { useApi } from "@api/useApi";
import { useWizard } from "@contexts/WizardContext";
import { useMobileSidebar } from "@contexts/MobileSidebarContext";
import {
  useGuardedLinkClick,
  useGuardedNavigate,
} from "@contexts/NavigationGuardContext";
import { useBoard, useBoards } from "@onetasklist/shared";
import {
  Button,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
} from "@fluentui/react-components";
import { AddRegular } from "@fluentui/react-icons/svg/add";
import { AlertOffRegular } from "@fluentui/react-icons/svg/alert-off";
import { AlertOnRegular } from "@fluentui/react-icons/svg/alert-on";
import { ChevronDownRegular } from "@fluentui/react-icons/svg/chevron-down";
import { PanelLeftRegular } from "@fluentui/react-icons/svg/panel-left";
import { usePushNotifications } from "@contexts/PushNotificationContext";
import { BoardIcon } from "../BoardCard/BoardIcon";
import { AppHelpMenu } from "./AppHelpMenu";
import AppHeaderNav from "./AppHeaderNav";
import { AppUserMenu } from "./AppUserMenu";
import { useAppHeaderViewModel } from "./AppHeader.ViewModel";
import FeedbackDialog from "../../features/feedback/FeedbackDialog";
import "./AppHeader.scss";

function AppHeader(): JSX.Element {
  const { t } = useTranslation();
  const { isConnected } = useSignalR();
  const { pushStatus, toggle: togglePush } = usePushNotifications();
  // Both leave whatever page is open, so both let it prompt first.
  const guardedNavigate = useGuardedNavigate();
  const guardedLinkClick = useGuardedLinkClick();
  const api = useApi();
  const { config } = useConfig();
  const { themeMode } = useThemeMode();
  const { openWizard } = useWizard();
  const {
    hasSidebar,
    isOpen: isMobileSidebarOpen,
    toggle: toggleMobileSidebar,
  } = useMobileSidebar();
  const iconBase = config?.regionBaseUrl ?? "";

  // Detect board routes — path pattern is /:boardId/tasks, /:boardId/settings etc.
  const boardMatch = useMatch("/:boardId/*");
  const { boardId, isEmbedded } = useAppHeaderViewModel({
    rawBoardId: boardMatch?.params.boardId,
  });

  const { data: board, isLoading: isBoardLoading } = useBoard(
    api.boards,
    boardId ?? "",
  );
  const { data: boards = [], isLoading: areBoardsLoading } = useBoards(
    api.boards,
  );

  // Until the current board and the list to switch to have both arrived there
  // is nothing to choose between, so the trigger spins instead of offering a
  // generic "Board" that reads as a live selection sitting on a real choice.
  const isBoardSwitcherLoading = isBoardLoading || areBoardsLoading;

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  // Light is the only theme with a white header, so it is the only one that
  // needs the dark-on-transparent logo; navy (classic) and dark both need the
  // inverted one.
  const isBrandHeader = themeMode === "classic";
  const logoSuffix = themeMode === "light" ? "" : "_inverted";

  return (
    <header
      className={`app-header${isBrandHeader ? " app-header--brand" : ""}${isEmbedded ? " app-header--embedded" : ""}`}
    >
      <a href="#main-content" className="skip-link">
        {t("layout.skipToMainContent")}
      </a>
      {/* Mobile sidebar toggle (hidden on desktop, and on routes with no sidebar to open) */}
      {hasSidebar && (
        <button
          className="app-header__mobile-toggle"
          onClick={toggleMobileSidebar}
          aria-label={t("layout.toggleNavigationAria")}
          aria-expanded={isMobileSidebarOpen}
        >
          <PanelLeftRegular />
        </button>
      )}

      {/* The host shows its own branding, and "/" is not a meaningful
          destination from inside a frame. */}
      {!isEmbedded && (
        <Link
          to="/"
          onClick={guardedLinkClick("/")}
          className="app-header__brand"
        >
          <img
            src={`${iconBase}/assets/img/logo_full_horizontal${logoSuffix}_32.png`}
            height={32}
            alt={t("layout.brandAlt")}
            className="app-header__logo--full"
          />
          <img
            src={`${iconBase}/assets/img/logo_32x32${logoSuffix}.png`}
            height={32}
            alt={t("layout.brandAlt")}
            className="app-header__logo--icon"
          />
        </Link>
      )}

      <div className="app-header__left">
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              className={
                isBoardSwitcherLoading
                  ? "app-header__board-switcher app-header__board-switcher--loading"
                  : "app-header__board-switcher"
              }
              title={t("common.labels.board")}
              disabled={isBoardSwitcherLoading}
            >
              {isBoardSwitcherLoading ? (
                <Spinner size="tiny" aria-label={t("common.labels.loading")} />
              ) : (
                <>
                  <BoardIcon
                    icon={board?.icon}
                    className="app-header__board-switcher-icon"
                  />
                  <span className="app-header__board-switcher-text">
                    {board?.name ?? t("common.labels.board")}
                  </span>
                  <ChevronDownRegular className="app-header__board-switcher-chevron" />
                </>
              )}
            </Button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {boards.map((availableBoard) => (
                <MenuItem
                  key={availableBoard.id}
                  icon={<BoardIcon icon={availableBoard.icon} />}
                  onClick={() => guardedNavigate(`/${availableBoard.id}/tasks`)}
                >
                  {availableBoard.name}
                </MenuItem>
              ))}
              <MenuDivider />
              <MenuItem icon={<AddRegular />} onClick={openWizard}>
                {t("layout.sidebar.newBoard")}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>

      <AppHeaderNav />

      {/* Right side — push bell + SignalR dot + user menu */}
      <div className="app-header__right">
        {/* Push notification bell — desktop only, hidden when not supported */}
        {pushStatus.isSupported && (
          <Button
            appearance="subtle"
            className="app-header__push-btn"
            onClick={togglePush}
            disabled={pushStatus.isInProgress}
            title={
              pushStatus.isSubscribed
                ? t("layout.push.turnOff")
                : t("layout.push.turnOn")
            }
            aria-label={
              pushStatus.isSubscribed
                ? t("layout.push.turnOff")
                : t("layout.push.turnOn")
            }
            icon={
              pushStatus.isSubscribed ? <AlertOnRegular /> : <AlertOffRegular />
            }
          />
        )}

        <span
          className={`app-header__signalr-dot${isConnected ? " app-header__signalr-dot--connected" : ""}`}
          title={
            isConnected
              ? t("layout.liveUpdates.connected")
              : t("layout.liveUpdates.disconnected")
          }
          aria-label={
            isConnected
              ? t("layout.liveUpdates.connected")
              : t("layout.liveUpdates.disconnected")
          }
          role="status"
        />

        {/* Theme and language live in the user menu, beside Account settings —
            they are preferences, not resources. Help duplicates chrome an
            embedding host already provides, so an embedded header leaves it out. */}
        {!isEmbedded && (
          <AppHelpMenu onSendFeedback={() => setIsFeedbackOpen(true)} />
        )}

        <AppUserMenu isEmbedded={isEmbedded} />
      </div>

      <FeedbackDialog
        isOpen={isFeedbackOpen}
        onOpenChange={setIsFeedbackOpen}
        boardId={boardId ?? null}
      />
    </header>
  );
}

export default AppHeader;
